<template>
  <div id="app" :class="{ contain: getSidebarStatus }">
    <vue-announcer></vue-announcer>
    <div class="fade-screen" v-if="getSidebarStatus" @click="toggleSidebar"></div>
    <Header></Header>
    <Sidebar></Sidebar>
    <main :class="{ indent: getSidebarStatus }">
      <Hero></Hero>
      <div class="page-content container">
        <router-view :key="$route.fullPath"></router-view>
      </div>
    </main>
  </div>
</template>

<script>
import Header from "@/components/Header.vue";
import Hero from "@/components/Hero.vue";
import Sidebar from "@/components/Sidebar.vue";

import "details-element-polyfill";
import { mapGetters, mapMutations } from "vuex";

export default {
  name: "app",
  components: {
    Header,
    Hero,
    Sidebar
  },

  mounted() {
    const vm = this;

    // Hide sidebar on escape press.
    window.addEventListener("keyup", event => {
      if (event.key === "Escape") vm.hideSidebar();
    });
  },

  computed: {
    ...mapGetters(["getSidebarStatus"])
  },

  methods: {
    ...mapMutations(["hideSidebar"]),

    // Hides the sidebar.
    toggleSidebar() {
      this.hideSidebar();
    }
  }
};
</script>

<style>
:root {
  --almost-black: #13191f;
  --battleship-grey: #637381;
  --brick: #9b301c;
  --dark: #212b36;
  --darkish-pink: #F24E88;
  --eggshell: #f6f6f6;
  --grey: #979797;
  --michigan-blue: #00274c;
  --michigan-maize: #ffcb05;
  --pale-grey: #e6e8ed;
  --topaz: #00b4ae;
  --white: #ffffff;
}

@font-face {
  font-family: Montserrat;
  src: url("/app/fonts/montserrat-regular.woff") format("woff"),
    url("/app/fonts/montserrat-regular.woff2") format("woff2");
}

@font-face {
  font-family: BebasNeue;
  src: url("/app/fonts/BebasNeue-Regular.woff") format("woff"),
    url("/app/fonts/BebasNeue-Regular.woff2") format("woff2");
}

@font-face {
  font-family: IBMPlexMono;
  src: url("/app/fonts/IBMPlexMono-Regular.woff") format("woff"),
    url("/app/fonts/IBMPlexMono-Regular.woff2") format("woff2");
}

* {
  box-sizing: border-box;
}

body {
  background-color: var(--eggshell);
  margin: 0;
}

img {
  height: auto;
  max-width: 100%;
  vertical-align: middle;
}

main {
  margin-left: 255px;
}

figure {
  margin: 0 1rem;
}

details {
  background-color: #fff;
  border-radius: 5px;
  box-shadow: 0 1px 5px 0 rgba(0, 0, 0, 0.16), 0 3px 4px 0 rgba(0, 0, 0, 0.16);
  margin-bottom: 1.5rem;
}

details[open] .arrow {
  transform: rotate(-90deg);
  transition: transform 0.15s;
}

summary {
  background-color: var(--battleship-grey);
  color: var(--eggshell);
  cursor: pointer;
  font-size: 1.125rem;
  font-weight: 600;
  list-style: none;
  padding: 1rem 2rem;
}

summary::before {
  content: "";
  padding: 0;
}

[open] > summary::before {
  content: "";
}

summary::-webkit-details-marker {
  display: none;
}

summary .arrow {
  float: right;
  transform: rotate(90deg);
  transition: transform 0.15s;
}

details.entry summary {
  background-color: var(--topaz);
  color: var(--almost-black);
}

details .details-content {
  padding: 2rem;
  position: relative;
}

details .need-log {
  font-size: 1rem;
  font-weight: 500;
  margin-left: 2rem;
}

table {
  border: 1px solid var(--michigan-blue);
  border-collapse: collapse;
  font-size: 14px;
}

table {
  width: 100%;
}

.contain {
  height: 100vh;
  overflow: hidden;
}

.table-wrapper {
  margin-bottom: 2rem;
  overflow-x: auto;
}

thead {
  background-color: var(--eggshell);
}

th {
  border: 1px solid var(--eggshell);
  padding: 1rem;
  text-align: left;
}

td {
  border: 1px solid var(--eggshell);
  padding: 1rem;
}

.field {
  margin-bottom: 1.5rem;
}

.field label {
  display: flex;
  align-items: center;
  font-weight: 500;
}

.field label span {
  display: inline-block;
  margin-right: 2rem;
  width: 45%;
}

input,
select {
  background-color: #fafafa;
  border: 1px solid var(--pale-grey);
  border-radius: 4px;
  font-family: IBMPlexMono, monospace;
  height: 32px;
  width: 125px;
  padding: 0.5rem;
}

input[type="range"] {
  -webkit-appearance: none;
  appearance: none;
  background-color: var(--pale-grey);
  border: 0;
  border-radius: 2px;
  height: 4px;
  padding: 0;
  width: 300px;
}

input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  background-color: var(--michigan-blue);
  border-radius: 50%;
  box-shadow: 0 0 0.25rem 0 rgba(0, 0, 0, 0.5);
  cursor: pointer;
  height: 1rem;
  width: 1rem;
}

input[type="range"]::-moz-range-thumb {
  background-color: var(--michigan-blue);
  border-radius: 50%;
  box-shadow: 0 0 0.25rem 0 rgba(0, 0, 0, 0.5);
  cursor: pointer;
  height: 1rem;
  width: 1rem;
}

input[type="range"]::-moz-range-progress {
  background-color: var(--michigan-blue);
}

input[type="range"]::-ms-fill-lower {
  background-color: var(--michigan-blue);
}

input[type="checkbox"],
input[type="radio"] {
  width: auto;
}

.range-container {
  position: relative;
}

.range-bubble {
  left: 0;
  position: absolute;
  text-align: center;
  top: -1.25rem;
  width: 1.5rem;
}

.range-labels {
  display: flex;
  font-size: 0.8125rem;
  justify-content: space-between;
  width: 100%;
}

.range-labels p {
  margin-bottom: 0;
}

.radio-wrapper {
  align-items: center;
  display: flex;
}

.radio-wrapper label {
  display: inline-block;
  margin-left: 0.5rem;
}

#app {
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  font-family: Montserrat, -apple-system, BlinkMacSystemFont, avenir next,
    avenir, helvetica neue, helvetica, ubuntu, roboto, noto, segoe ui, arial,
    sans-serif;
}

.container {
  margin: 0 auto;
  max-width: 960px;
  padding-left: 1rem;
  padding-right: 1rem;
}

.panel {
  background-color: var(--white);
  border-radius: 5px;
  box-shadow: 0 0 31px 0 rgba(0, 0, 0, 0.24);
}

.page-content {
  margin-top: -8rem;
  position: relative;
  z-index: 1;
}

.fade-enter-active {
  transition: all 0.5s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-active {
  transition: all 0.5s;
}
.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(1rem);
}

.button {
  cursor: pointer;
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.5rem 2rem;
  text-decoration: none;
  text-transform: uppercase;
}

.button--standard {
  background-color: var(--michigan-blue);
  border: 1px solid var(--michigan-blue);
  color: var(--white);
}

.button--danger {
  background-color: transparent;
  border: 1px solid var(--brick);
  color: var(--brick);
}

.checkbox {
  align-items: center;
  display: flex;
}

.checkbox input {
  margin-right: 1rem;
}

.filters {
  display: flex;
  justify-content: space-between;
  text-align: center;
}

.filter {
  background-color: var(--michigan-maize);
  flex-basis: 50%;
  margin-left: 1px;
  padding: 1rem;
  color: var(--michigan-blue);
  font-size: 1.25rem;
  text-decoration: none;
  font-weight: 600;
  letter-spacing: -0.1px;
}

.filter.active {
  background-color: var(--white);
}

.filter:first-child {
  border-top-left-radius: 5px;
  margin-left: 0;
}

.filter:last-child {
  border-top-right-radius: 5px;
}

.custom-filter {
  align-items: flex-end;
  display: flex;
  margin: 1rem;
  justify-content: space-between;
}

.custom-filter label {
  flex-basis: 38%;
  text-align: center;
}

.custom-filter input[type="submit"] {
  flex-basis: 20%;
}

.custom-filter input[type="date"] {
  text-align: center;
  width: 100%;
}

.custom-filter label span {
  display: block;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.charts {
  margin-bottom: 3rem;
  padding-bottom: 1rem;
  overflow: hidden;
}

.date-change {
  align-items: center;
  color: #494949;
  display: flex;
  font-size: 1.125rem;
  justify-content: center;
  margin-top: 2rem;
}

.date-change span {
  margin-top: 1px;
}

.date-change a {
  display: inline-block;
}

.previous-dates {
  margin-right: 1rem;
}

.next-dates {
  margin-left: 1rem;
}

.previous-dates img {
  transform: rotate(180deg);
}

@media all and (max-width: 800px) {
  main {
    margin-left: 0;
    transition: transform 0.25s ease-out;
  }
  main.indent {
    transform: translateX(255px);
    transition: transform 0.25s ease-in;
  }
  .fade-screen {
    background-color: rgba(11, 15, 18, 0.5);
    bottom: 0;
    position: fixed;
    left: 0;
    right: 0;
    top: 0;
    z-index: 3;
  }
}

@media all and (max-width: 640px) {
  summary {
    font-size: 0.875rem;
    padding-left: 1rem;
  }
  summary .need-log {
    font-size: 0.75rem;
    margin-left: 1rem;
  }
  .filter,
  .date-change {
    font-size: 0.875rem;
  }
  .date-change {
    font-size: 0.75rem;
  }
  .field {
    font-size: 0.875rem;
  }
  .field label {
    display: block;
  }
  label span {
    margin-bottom: 0.5rem;
    width: 100%;
  }
  input,
  select,
  input[type="range"] {
    width: 100%;
  }
  .custom-filter {
    display: block;
  }
  .custom-filter label {
    display: block;
    font-size: 0.875rem;
    margin-bottom: 1rem;
  }
}
</style>
